import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/cn';
import { Select } from './Select';

export interface YearPickerProps {
  year: number;
  onChange: (year: number) => void;
  /** Selectable years, ascending or descending — order is preserved. */
  years: number[];
  ariaLabel?: string;
  className?: string;
}

/**
 * The one year stepper, for reports whose period is a whole year (Budgets,
 * P&L). Same shell as {@link MonthPicker} — bordered strip, chevrons either
 * side of the label — so the two report screens you flip between don't offer
 * two different-looking ways to change the period.
 *
 * The chevrons are the fast path (last year / next year); the select is the
 * jump. Steps clamp to the ends of `years` rather than disappearing.
 */
export function YearPicker({
  year,
  onChange,
  years,
  ariaLabel = 'Report year',
  className,
}: YearPickerProps) {
  const sorted = [...years].sort((a, b) => a - b);
  const min = sorted[0] ?? year;
  const max = sorted[sorted.length - 1] ?? year;
  return (
    <div
      className={cn(
        'inline-flex items-center gap-1 rounded-lg border border-grey-200 bg-white p-0.5',
        className,
      )}
      role="group"
      aria-label={ariaLabel}
    >
      <button
        type="button"
        aria-label={`Go to ${year - 1}`}
        disabled={year <= min}
        onClick={() => onChange(year - 1)}
        className="btn-icon text-grey-500 hover:text-secondary disabled:opacity-30"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden />
      </button>
      <Select
        aria-label={ariaLabel}
        selectSize="sm"
        className="w-24 [&_select]:border-0 [&_select]:bg-transparent [&_select]:font-semibold [&_select]:shadow-none"
        value={String(year)}
        onChange={(e) => onChange(Number(e.target.value))}
        options={years.map((y) => ({ value: String(y), label: String(y) }))}
      />
      <button
        type="button"
        aria-label={`Go to ${year + 1}`}
        disabled={year >= max}
        onClick={() => onChange(year + 1)}
        className="btn-icon text-grey-500 hover:text-secondary disabled:opacity-30"
      >
        <ChevronRight className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
}
